import { useCallback, useEffect, useRef } from 'react'
import { IEquipment, IEquipmentEvent } from '../equipment.types'
import equipmentApi from '../equipment.api'
import useHttpLoader from '@/shared/hooks/useHttpLoader'
import timeUtils from '@/utils/time.utils'

export interface IUseErrorEventsProps {
  equipmentId: IEquipment['id']
  tsFrom: number
  tsTo?: number
  disabled?: boolean
  eventTimeMarginSeconds?: number
  onNewValueReceived?: () => void
}

const useErrorEventsRef = (props: IUseErrorEventsProps) => {
  const { wait, loading } = useHttpLoader()
  const eventsRef = useRef<IEquipmentEvent[]>([])

  const fetchRequest = useCallback(() => {
    if (!props.equipmentId) return

    equipmentApi.getEquipmentEventsAbortable.abort()

    wait(
      equipmentApi.getEquipmentEventsAbortable({
        limit: 200,
        created_gte: timeUtils.subSeconds(props.tsFrom, props.eventTimeMarginSeconds ?? 1).toISOString(),
        created_lte: props.tsTo
          ? new Date(props.tsTo).toISOString()
          : timeUtils.addSeconds(props.tsFrom, props.eventTimeMarginSeconds ?? 1).toISOString(),
        equipmentId: props.equipmentId,
      }),
      (resp) => {
        if (resp.status === 'success') {
          eventsRef.current = resp.body.items
          props.onNewValueReceived()
        }
      }
    )
  }, [props.equipmentId, props.tsFrom, props.tsTo, props.onNewValueReceived])

  useEffect(() => {
    if (!props.equipmentId || props.disabled) return
    fetchRequest()
  }, [fetchRequest, props.equipmentId, props.tsFrom, props.tsTo])

  return {
    eventsRef,
    loading,
  }
}

export default useErrorEventsRef
